<template>
  <div class="mx-auto mt-48 flex max-w-screen flex-col gap-24 pt-10 md:mb-24 md:px-10 md:pb-48">
    <!-- Header and feature buttons -->
    <div class="flex flex-col items-center px-3">
      <p class="mb-4 text-lg text-zinc-500">{{ t('HomeFeatures.label') }}</p>
      <p class="font-family-display text-center text-[40px] leading-none md:mb-10">{{ t('HomeFeatures.heading') }}</p>

      <div class="hidden w-full grid-cols-4 gap-6 px-3 md:grid">
        <Button
          v-for="feature in features"
          :key="feature.name"
          :label="feature.name"
          severity="secondary"
          class="w-full rounded-2xl text-zinc-800 min-[1081px]:text-xl"
          :class="[
            feature.active ? feature.buttonActiveClass : 'border-zinc-100 bg-zinc-100',
            !feature.active ? feature.buttonHoverBorder : '',
          ]"
          @click="onClick(feature)"
        />
      </div>
    </div>

    <!-- Feature cards -->
    <Card
      v-for="feature in features"
      :key="feature.name"
      :class="{ hidden: !feature.active }"
      class="w-full shadow-none md:rounded-3xl md:border md:border-zinc-100 md:shadow-xl"
      :pt="{ body: 'p-0', content: 'flex flex-col-reverse items-center md:grid md:grid-rows-1 md:grid-cols-2' }"
    >
      <template #content>
        <!-- Feature Text Content -->
        <div class="flex flex-col p-5 md:p-10">
          <p class="font-family-display mb-4 text-[40px] font-semibold">{{ feature.name }}</p>
          <p class="mb-6 text-lg">{{ feature.description }}</p>
          <ul class="mb-6 flex flex-col gap-4">
            <li v-for="point in feature.bulletPoints" :key="point.text" class="flex items-center gap-3">
              <i class="pi pi-check text-indigo-400"></i>
              <p class="text-lg">{{ point.text }}</p>
            </li>
          </ul>
          <Button v-slot="slotProps" as-child severity="primary" size="large">
            <NuxtLink
              :to="localePath({ name: 'contact-us' })"
              class="w-fit hover:no-underline"
              :class="slotProps.class"
            >
              {{ t('HomeFeatures.cta') }}
            </NuxtLink>
          </Button>
        </div>

        <!-- Mobile Feature Select -->
        <div class="w-full max-w-sm px-5 md:hidden">
          <Select
            id="feature-select"
            v-model="selectedFeature"
            :options="features"
            option-label="name"
            :placeholder="activeFeature.name"
            class="w-full rounded-full bg-violet-500 px-6 py-4 placeholder:text-white"
            :pt="{
              dropdownIcon: { class: 'text-white' },
              overlay: { class: 'mt-0.5 rounded-2xl' },
            }"
            @change="onDropdownChange"
          >
            <template #value="slotProps">
              <p class="text-[20px] text-white">{{ slotProps.value?.name }}</p>
            </template>
            <template #option="slotProps">
              <p class="text-[20px]">{{ slotProps.option?.name }}</p>
            </template>
          </Select>
        </div>

        <!-- Feature Image Content -->
        <div
          class="mb-6 flex min-h-full w-full items-center justify-center p-5 md:mb-0 md:rounded-3xl md:p-3 lg:p-10"
          :class="feature.bgColor"
        >
          <img
            alt=""
            :src="renderImage(`${feature.imageUrl}`, { quality: 100, height: 400 })"
            class="my-auto w-full max-w-[450px]"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import Select from 'primevue/select';
import Card from 'primevue/card';
import renderImage from '~/utils/render-image';

const localePath = useLocalePath();
const { t } = useI18n();

interface BulletPoint {
  text: string;
}

interface Feature {
  key: string;
  name: string;
  description: string;
  imageUrl: string;
  bgColor: string;
  buttonActiveClass: string;
  buttonHoverBorder: string;
  active: boolean;
  bulletPoints: BulletPoint[];
}

const activeFeatureKey = ref('customWebpage');

const features = computed<Feature[]>(() => [
  {
    key: 'customWebpage',
    name: t('HomeFeatures.customWebpage.name'),
    description: t('HomeFeatures.customWebpage.description'),
    bulletPoints: [
      { text: t('HomeFeatures.customWebpage.bullet1') },
      { text: t('HomeFeatures.customWebpage.bullet2') },
      { text: t('HomeFeatures.customWebpage.bullet3') },
    ],
    bgColor: 'bg-green-100',
    buttonActiveClass: 'bg-green-100 border-green-100',
    buttonHoverBorder: 'hover:border-green-300',
    imageUrl: 'RaffleboxLogos/WebsiteAssets/Custom-webpage-screenshot.png',
    active: activeFeatureKey.value === 'customWebpage',
  },
  {
    key: 'automatedEmails',
    name: t('HomeFeatures.automatedEmails.name'),
    description: t('HomeFeatures.automatedEmails.description'),
    bulletPoints: [
      { text: t('HomeFeatures.automatedEmails.bullet1') },
      { text: t('HomeFeatures.automatedEmails.bullet2') },
    ],
    bgColor: 'bg-orange-100',
    buttonActiveClass: 'bg-orange-100 border-orange-100',
    buttonHoverBorder: 'hover:border-orange-300',
    imageUrl: 'RaffleboxLogos/WebsiteAssets/custom-email.png',
    active: activeFeatureKey.value === 'automatedEmails',
  },
  {
    key: 'salesTracking',
    name: t('HomeFeatures.salesTracking.name'),
    description: t('HomeFeatures.salesTracking.description'),
    bulletPoints: [
      { text: t('HomeFeatures.salesTracking.bullet1') },
      { text: t('HomeFeatures.salesTracking.bullet2') },
      { text: t('HomeFeatures.salesTracking.bullet3') },
    ],
    bgColor: 'bg-rose-100',
    buttonActiveClass: 'bg-rose-100 border-rose-100',
    buttonHoverBorder: 'hover:border-rose-300',
    imageUrl: 'RaffleboxLogos/WebsiteAssets/top-fundraisers-screenshot.jpg',
    active: activeFeatureKey.value === 'salesTracking',
  },
  {
    key: 'charityDashboard',
    name: t('HomeFeatures.charityDashboard.name'),
    description: t('HomeFeatures.charityDashboard.description'),
    bulletPoints: [
      { text: t('HomeFeatures.charityDashboard.bullet1') },
      { text: t('HomeFeatures.charityDashboard.bullet2') },
      { text: t('HomeFeatures.charityDashboard.bullet3') },
    ],
    bgColor: 'bg-violet-200',
    buttonActiveClass: 'bg-violet-200 border-violet-200',
    buttonHoverBorder: 'hover:border-violet-300',
    imageUrl: 'RaffleboxLogos/WebsiteAssets/dashboard-screenshot.jpg',
    active: activeFeatureKey.value === 'charityDashboard',
  },
]);

const selectedFeature = ref<Feature | null>(null);

const activeFeature = computed(() => {
  const found = features.value.find((f) => f.active);
  return (found ?? features.value[0]) as Feature;
});

onMounted(() => {
  selectedFeature.value = activeFeature.value;
});

const onClick = (feature: Feature) => {
  activeFeatureKey.value = feature.key;
  selectedFeature.value = feature;
};

const onDropdownChange = () => {
  if (selectedFeature.value) {
    onClick(selectedFeature.value);
  }
};
</script>
